/* =========================================================================
   INO x Horizon - ino-product.js
   Behavior layer for the bespoke INO product page (template product.ino).
   Loaded alongside ino-home.js (which owns nav / reveal / specsheet / howfolds
   / reviews-carousel). This file owns only what is unique to the PDP:
     - the 2-up gallery (per-colorway, open/folded image sets, arrows, drag)
     - the colour swatches wired to REAL Shopify variants (price + add button)
     - the open / folded toggle
     - the FAQ accordion
     - the sticky add-to-cart bar
     - a REAL add-to-cart: POST /cart/add (+ Section Rendering), then dispatch
       Horizon's `cart:update` so its cart drawer refreshes, and open the drawer.
   Idempotent + theme-editor safe + defensive: missing nodes never throw.
   ========================================================================= */
(function () {
  "use strict";

  function parseData(section) {
    try {
      var el = section.querySelector("[data-ino-pdp-data]");
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  }

  /* ---- INO nav bag count: set + reveal (the span may not exist when the
     cart loaded empty, so create it). ------------------------------------ */
  function updateBagCount(count) {
    var bags = document.querySelectorAll("[data-ino-cart]");
    for (var i = 0; i < bags.length; i++) {
      var bag = bags[i];
      var badge = bag.querySelector(".bag-count");
      if (!badge && count > 0) {
        badge = document.createElement("span");
        badge.className = "bag-count";
        bag.appendChild(badge);
      }
      if (badge) {
        badge.textContent = count;
        badge.style.display = count > 0 ? "inline-flex" : "none";
      }
    }
  }

  /* ---- Mirror Horizon's CartAddEvent so cart-items-component re-renders
     from the freshly rendered sections, and the drawer (auto-open) reacts. */
  function dispatchCartUpdate(cart, sections) {
    try {
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: cart || undefined,
            sourceId: "ino-pdp",
            data: {
              source: "ino-pdp",
              itemCount: cart ? cart.item_count : undefined,
              sections: sections,
            },
          },
        })
      );
    } catch (e) {}
  }

  function initPdp(section) {
    if (section.dataset.inoPdpBound) return;
    section.dataset.inoPdpBound = "1";

    var data = parseData(section);
    if (!data) return;
    var colorways = data.colorways || [];
    if (!colorways.length) return;

    var reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion:reduce)").matches;

    var slidesEl = section.querySelector("[data-ino-slides]");
    var countEl = section.querySelector("[data-ino-count]");
    var hiddenId = section.querySelector("[data-ino-variant-id]");
    var priceEl = section.querySelector("[data-ino-price]");
    var addBtn = section.querySelector("[data-ino-add]");
    var addLabel = section.querySelector("[data-ino-add-label]");
    var cwName = section.querySelector("[data-ino-cw-name]");
    var seg = section.querySelector("[data-ino-vseg]");
    var form = section.querySelector("form.ino-pdp-form");
    var stickyThumb = section.querySelector("[data-ino-sticky-thumb]");
    var stickyName = section.querySelector("[data-ino-sticky-name]");
    var stickyPrice = section.querySelector("[data-ino-sticky-price]");

    var ci = 0; // colorway index
    var state = "open"; // "open" | "fold"
    var idx = 0; // slide index
    var firstPaint = true; // keep the server-rendered slides on the first render pass
    var addPrefix = data.addLabel || "Add to Bag";

    function cw() {
      return colorways[ci];
    }
    function curList() {
      var c = cw();
      var list = state === "fold" ? c.fold : c.open;
      if (!list || !list.length) list = c.open && c.open.length ? c.open : null;
      if (!list || !list.length) list = data.fallback || [];
      return list && list.length ? list : [];
    }
    // Captions that travel with c.open (the section emits them in the page's language).
    // Only valid for the "open" list; a fold list has no captions, so fall back to "".
    function curAlts() {
      var c = cw();
      var usingOpen = !(state === "fold" && c.fold && c.fold.length);
      return usingOpen && c.alts && c.alts.length ? c.alts : [];
    }

    /* ---- gallery render + navigation (ported math from the comp) ---- */
    function slideW() {
      var s = slidesEl ? slidesEl.querySelector(".slide") : null;
      return s
        ? s.getBoundingClientRect().width
        : slidesEl
        ? slidesEl.clientWidth || 1
        : 1;
    }
    function visible() {
      if (!slidesEl) return 1;
      return Math.max(1, Math.round((slidesEl.clientWidth || 1) / slideW()));
    }
    function nSlides() {
      return curList().length;
    }
    function maxIdx() {
      return Math.max(0, nSlides() - visible());
    }
    function clampIdx(i) {
      return Math.max(0, Math.min(maxIdx(), i));
    }
    function paintCount() {
      if (countEl) countEl.textContent = idx + 1 + " / " + nSlides();
    }
    function gapPx() {
      if (!slidesEl) return 0;
      var cs = window.getComputedStyle(slidesEl);
      return parseFloat(cs.columnGap || cs.gap || "0") || 0;
    }
    function stepW() { return slideW() + gapPx(); } // slide width + the gap between
    function smoothScrollTo(left, duration) {
      if (!slidesEl) return;
      var startLeft = slidesEl.scrollLeft;
      var dist = left - startLeft;
      // Hold scroll-snap OFF through the whole settle so the browser's instant
      // "mandatory" snap can't jerk the eased glide (the old abrupt "tak"). Inline
      // style beats the CSS rule; we restore it - already aligned - when done.
      slidesEl.style.scrollSnapType = "none";
      if (Math.abs(dist) < 1 || reduce) {
        slidesEl.scrollLeft = left;
        slidesEl.style.scrollSnapType = "";
        paintCount();
        return;
      }
      var startT = null;
      function step(t) {
        if (startT === null) startT = t;
        var p = Math.min(1, (t - startT) / duration);
        var ease = 1 - Math.pow(1 - p, 3); // easeOutCubic - gentle, slightly slow settle
        slidesEl.scrollLeft = startLeft + dist * ease;
        if (p < 1) requestAnimationFrame(step);
        else slidesEl.style.scrollSnapType = "";
      }
      requestAnimationFrame(step);
    }
    function scrollToIdx(smooth) {
      if (!slidesEl) return;
      // offsetLeft accounts for the gap automatically, so arrows land precisely.
      var target = slidesEl.children[idx];
      var left = target ? target.offsetLeft : idx * stepW();
      if (smooth && !reduce) smoothScrollTo(left, 560);
      else slidesEl.scrollLeft = left;
      paintCount();
    }
    function esc(s) {
      return String(s || "").replace(/"/g, "&quot;");
    }
    function renderSlides() {
      if (!slidesEl) return;
      // The section renders the slides server-side (real alt, first image eager). Keep that
      // markup on the FIRST paint; every later call (colourway / open-fold switch) re-renders.
      if (firstPaint && slidesEl.querySelector(".slide")) {
        firstPaint = false;
        idx = 0;
        paintCount();
        return;
      }
      firstPaint = false;
      var list = curList();
      var alts = curAlts();
      var name = cw().label || "";
      slidesEl.innerHTML = list
        .map(function (url, i) {
          return (
            '<div class="slide"><img src="' +
            esc(url) +
            '" alt="' +
            esc(alts[i] || name + " - view " + (i + 1)) +
            '" loading="lazy"></div>'
          );
        })
        .join("");
      idx = 0;
      slidesEl.scrollLeft = 0;
      paintCount();
    }
    function go(dir) {
      idx = clampIdx(idx + dir);
      scrollToIdx(true);
    }

    /* ---- variant / colorway apply ---- */
    function applyColorway() {
      var c = cw();
      if (hiddenId && c.variantId) hiddenId.value = c.variantId;
      var price = c.price || data.price || "";
      // innerHTML (not textContent): the Shopify `money` filter can emit the
      // currency symbol as an HTML entity (e.g. &#8362; for ₪) - innerHTML decodes it.
      if (priceEl) priceEl.innerHTML = price;
      if (cwName) cwName.textContent = c.label || "";
      if (stickyName) stickyName.textContent = c.label || "";
      if (stickyPrice) stickyPrice.innerHTML = price;
      if (stickyThumb) {
        var thumb = c.thumb || (c.open && c.open[0]) || (data.fallback || [])[0];
        if (thumb) stickyThumb.src = thumb;
      }
      var available = c.available !== false;
      if (addBtn) {
        addBtn.disabled = !available;
        if (addLabel)
          addLabel.innerHTML = available
            ? addPrefix + (price ? " · " + price : "")
            : data.soldOut || "Sold out";
      }
      // Keep the phone sticky bar in step with the real button: a sold-out
      // edition must not offer a live "Add to Bag" that adds nothing.
      if (stickyAdd && !stickyBusy) {
        stickyAdd.disabled = !available;
        var stickyLabel = stickyAdd.querySelector("[data-ino-sticky-label]");
        if (stickyLabel)
          stickyLabel.innerHTML = available
            ? addPrefix + (price ? ' · <span data-ino-sticky-price>' + price + "</span>" : "")
            : stickyAdd.getAttribute("data-sold-out-label") || data.soldOut || "Sold out";
        stickyHtml = stickyAdd.innerHTML;
      }
      // active states
      var sw = section.querySelectorAll("[data-ino-swatch]");
      for (var i = 0; i < sw.length; i++)
        sw[i].classList.toggle("active", String(i) === String(ci));
      var mini = section.querySelectorAll("[data-ino-mini]");
      for (var j = 0; j < mini.length; j++)
        mini[j].classList.toggle("active", String(j) === String(ci));
    }
    function setColor(i) {
      if (i === ci || i < 0 || i >= colorways.length) return;
      ci = i;
      renderSlides();
      applyColorway();
    }
    function setState(s) {
      if (s === state) return;
      state = s;
      if (seg) seg.classList.toggle("is-fold", state === "fold");
      renderSlides();
    }

    /* ---- wire gallery controls ---- */
    var prev = section.querySelector("[data-ino-prev]");
    var next = section.querySelector("[data-ino-next]");
    if (prev) prev.addEventListener("click", function () { go(-1); });
    if (next) next.addEventListener("click", function () { go(1); });

    if (slidesEl) {
      slidesEl.addEventListener(
        "scroll",
        function () {
          idx = Math.max(0, Math.min(maxIdx(), Math.round(slidesEl.scrollLeft / stepW())));
          paintCount();
        },
        { passive: true }
      );
      // pointer drag (mouse) for a tangible flip
      var down = false, startX = 0, startScroll = 0;
      slidesEl.addEventListener("pointerdown", function (e) {
        if (e.pointerType !== "mouse") return;
        down = true; startX = e.clientX; startScroll = slidesEl.scrollLeft;
        slidesEl.classList.add("dragging");
        try { slidesEl.setPointerCapture(e.pointerId); } catch (x) {}
      });
      slidesEl.addEventListener("pointermove", function (e) {
        if (!down) return;
        slidesEl.scrollLeft = startScroll - (e.clientX - startX);
      });
      function endDrag() {
        if (!down) return;
        down = false;
        slidesEl.style.scrollSnapType = "none"; // hold snap off until the eased settle finishes
        slidesEl.classList.remove("dragging");
        idx = clampIdx(Math.round(slidesEl.scrollLeft / stepW()));
        scrollToIdx(true);
      }
      slidesEl.addEventListener("pointerup", endDrag);
      slidesEl.addEventListener("pointercancel", endDrag);
    }

    var gallery = section.querySelector("[data-ino-gallery]");
    if (gallery) {
      gallery.addEventListener("keydown", function (e) {
        if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
        else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      });
    }

    /* ---- image lightbox / zoom (click an image → full screen) ----
       Desktop: click (not drag) opens; click image toggles one zoom step; drag to pan
       when zoomed; arrows + Esc + X; backdrop closes. Mobile: tap opens, same controls.
       The overlay is appended to <body> so its position:fixed is NOT trapped by the
       PDP section's reveal transform (same containing-block rule as the sticky bar). */
    (function setupLightbox() {
      if (!slidesEl) return;
      var staleLb = document.querySelectorAll("body > .ino-lb");
      for (var q = 0; q < staleLb.length; q++) staleLb[q].remove();

      var lb = document.createElement("div");
      lb.className = "ino-lb";
      lb.setAttribute("role", "dialog");
      lb.setAttribute("aria-modal", "true");
      lb.setAttribute("aria-label", "Image viewer");
      lb.hidden = true;
      lb.innerHTML =
        '<button type="button" class="ino-lb-x" aria-label="Close">&times;</button>' +
        '<div class="ino-lb-stage"><img class="ino-lb-img" alt=""></div>' +
        '<div class="ino-lb-bar">' +
          '<button type="button" class="ino-lb-zoom" aria-label="Zoom">+</button>' +
          '<span class="ino-lb-divider" aria-hidden="true"></span>' +
          '<button type="button" class="ino-lb-nav ino-lb-prev" aria-label="Previous image">&larr;</button>' +
          '<span class="ino-lb-count"></span>' +
          '<button type="button" class="ino-lb-nav ino-lb-next" aria-label="Next image">&rarr;</button>' +
        '</div>';
      document.body.appendChild(lb);

      var lbImg = lb.querySelector(".ino-lb-img");
      var lbCount = lb.querySelector(".ino-lb-count");
      var lbZoom = lb.querySelector(".ino-lb-zoom");
      var lbIdx = 0, zoomed = false, panX = 0, panY = 0, inoCur = null;

      function applyTransform() {
        lbImg.style.transform = "translate(" + panX + "px," + panY + "px) scale(" + (zoomed ? 2 : 1) + ")";
      }
      function resetZoom() {
        zoomed = false; panX = 0; panY = 0;
        lbImg.style.transition = "";
        lbImg.style.transform = "";
        lb.classList.remove("is-zoomed");
        if (lbZoom) lbZoom.textContent = "+";
      }
      function loadImg() {
        var list = curList();
        if (!list.length) return;
        var alts = curAlts();
        lbIdx = (lbIdx + list.length) % list.length;
        resetZoom();
        lbImg.src = list[lbIdx];
        lbImg.alt = alts[lbIdx] || (cw().label || "") + " - view " + (lbIdx + 1);
        if (lbCount) lbCount.textContent = lbIdx + 1 + " / " + list.length;
      }
      function openLb(i) {
        lbIdx = i || 0;
        loadImg();
        lb.hidden = false;
        requestAnimationFrame(function () { lb.classList.add("open"); });
        document.body.style.overflow = "hidden";
        if (inoCur) inoCur.classList.remove("on"); // hide the follow-cursor behind the overlay
      }
      function closeLb() {
        lb.classList.remove("open");
        document.body.style.overflow = "";
        resetZoom();
        idx = clampIdx(lbIdx); scrollToIdx(false); // sync inline gallery to last viewed
        setTimeout(function () { if (!lb.classList.contains("open")) lb.hidden = true; }, 360);
      }
      function toggleZoom() {
        zoomed = !zoomed;
        if (!zoomed) { panX = 0; panY = 0; }
        lbImg.style.transition = ""; // animate the zoom
        lb.classList.toggle("is-zoomed", zoomed);
        if (lbZoom) lbZoom.textContent = zoomed ? "−" : "+";
        if (inoCur) inoCur.textContent = zoomed ? "−" : "+";
        applyTransform();
      }

      lb.querySelector(".ino-lb-x").addEventListener("click", function (e) { e.stopPropagation(); closeLb(); });
      lb.querySelector(".ino-lb-prev").addEventListener("click", function (e) { e.stopPropagation(); lbIdx--; loadImg(); });
      lb.querySelector(".ino-lb-next").addEventListener("click", function (e) { e.stopPropagation(); lbIdx++; loadImg(); });
      if (lbZoom) lbZoom.addEventListener("click", function (e) { e.stopPropagation(); toggleZoom(); });
      lb.addEventListener("click", function (e) {
        if (e.target === lb || (e.target.classList && e.target.classList.contains("ino-lb-stage"))) closeLb();
      });

      // image: drag to pan when zoomed; a clean tap toggles zoom
      var d = false, moved = false, sx = 0, sy = 0, bx = 0, by = 0;
      lbImg.addEventListener("pointerdown", function (e) {
        d = true; moved = false; sx = e.clientX; sy = e.clientY; bx = panX; by = panY;
        if (zoomed) lbImg.style.transition = "none"; // instant pan
        try { lbImg.setPointerCapture(e.pointerId); } catch (x) {}
      });
      lbImg.addEventListener("pointermove", function (e) {
        if (!d) return;
        var dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) moved = true;
        if (zoomed) {
          var maxX = lbImg.offsetWidth / 2, maxY = lbImg.offsetHeight / 2;
          panX = Math.max(-maxX, Math.min(maxX, bx + dx));
          panY = Math.max(-maxY, Math.min(maxY, by + dy));
          applyTransform();
        }
      });
      lbImg.addEventListener("pointerup", function () {
        if (!d) return; d = false;
        if (!moved) toggleZoom();
      });
      lbImg.addEventListener("pointercancel", function () { d = false; });

      if (window.__inoLbKey) document.removeEventListener("keydown", window.__inoLbKey);
      window.__inoLbKey = function (e) {
        if (lb.hidden) return;
        if (e.key === "Escape") closeLb();
        else if (e.key === "ArrowLeft") { lbIdx--; loadImg(); }
        else if (e.key === "ArrowRight") { lbIdx++; loadImg(); }
      };
      document.addEventListener("keydown", window.__inoLbKey);

      // OPEN trigger on the gallery: a click that is NOT a drag/swipe
      var gDown = false, gMoved = false, gSX = 0, gSY = 0;
      slidesEl.addEventListener("pointerdown", function (e) { gDown = true; gMoved = false; gSX = e.clientX; gSY = e.clientY; });
      slidesEl.addEventListener("pointermove", function (e) {
        if (!gDown) return;
        if (Math.abs(e.clientX - gSX) > 8 || Math.abs(e.clientY - gSY) > 8) gMoved = true;
      });
      window.addEventListener("pointerup", function () { gDown = false; });
      slidesEl.addEventListener("click", function (e) {
        if (gMoved) { gMoved = false; return; } // it was a drag/swipe
        // Open the slide the click actually landed in. The <img> is pointer-events:none,
        // so e.target can be the track instead of the slide; resolving by the click's
        // geometry is robust to that AND to RTL. Falls back to closest(), then idx.
        var i = -1, kids = slidesEl.children;
        for (var k = 0; k < kids.length; k++) {
          var r = kids[k].getBoundingClientRect();
          if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) { i = k; break; }
        }
        if (i < 0) {
          var slide = e.target.closest ? e.target.closest(".slide") : null;
          if (slide) i = Array.prototype.indexOf.call(slidesEl.children, slide);
        }
        openLb(i < 0 ? idx : i);
      });

      /* ---- custom "+" cursor: follows the mouse over the gallery (hover devices only).
         The native cursor is hidden over the viewport via CSS; this dot rides the pointer
         as the zoom affordance, exactly like On Cloud. Touch devices get nothing (tap opens). */
      var canHover = window.matchMedia && window.matchMedia("(hover: hover)").matches;
      if (canHover) {
        var staleCur = document.querySelectorAll("body > .ino-cursor");
        for (var c2 = 0; c2 < staleCur.length; c2++) staleCur[c2].remove();
        inoCur = document.createElement("div");
        inoCur.className = "ino-cursor";
        inoCur.textContent = "+";
        document.body.appendChild(inoCur);
        var moveCur = function (e, glyph) {
          if (e.pointerType && e.pointerType !== "mouse") return;
          inoCur.textContent = glyph;
          inoCur.style.left = e.clientX + "px";
          inoCur.style.top = e.clientY + "px";
          inoCur.classList.add("on");
        };
        var hideCur = function () { inoCur.classList.remove("on"); };
        var vp = section.querySelector(".viewport");
        if (vp) {
          vp.addEventListener("pointermove", function (e) {
            // while dragging the carousel, hide the + dot so the native grab hand shows
            if (slidesEl.classList.contains("dragging")) { hideCur(); return; }
            moveCur(e, "+");
          });
          vp.addEventListener("pointerleave", hideCur);
        }
        // the SAME follow-cursor inside the lightbox (replaces the OS magnifier);
        // shows + when not zoomed, − when zoomed.
        var lbStage = lb.querySelector(".ino-lb-stage");
        if (lbStage) {
          lbStage.addEventListener("pointermove", function (e) { moveCur(e, zoomed ? "−" : "+"); });
          lbStage.addEventListener("pointerleave", hideCur);
        }
      }
    })();

    // open / folded toggle
    if (seg) {
      var segBtns = seg.querySelectorAll("button");
      for (var s = 0; s < segBtns.length; s++) {
        (function (btn) {
          btn.addEventListener("click", function () {
            for (var b = 0; b < segBtns.length; b++) {
              var on = segBtns[b] === btn;
              segBtns[b].classList.toggle("active", on);
              segBtns[b].setAttribute("aria-pressed", on ? "true" : "false");
            }
            setState(btn.getAttribute("data-state"));
          });
        })(segBtns[s]);
      }
    }

    // swatches + sticky mini swatches
    var swatches = section.querySelectorAll("[data-ino-swatch]");
    for (var w = 0; w < swatches.length; w++) {
      (function (el, i) {
        el.addEventListener("click", function () { setColor(i); });
      })(swatches[w], w);
    }
    var minis = section.querySelectorAll("[data-ino-mini]");
    for (var m = 0; m < minis.length; m++) {
      (function (el, i) {
        el.addEventListener("click", function () { setColor(i); });
      })(minis[m], m);
    }

    /* ---- REAL add to cart ---- */
    var busy = false;
    function flashLabel(text, revert) {
      if (!addLabel) return;
      var original = addLabel.textContent;
      addLabel.textContent = text;
      if (revert) setTimeout(function () { applyColorway(); }, 1400);
    }
    function addToCart() {
      if (busy) return;
      var variantId = hiddenId && hiddenId.value;
      if (!variantId) return;
      busy = true;
      flashLabel(data.adding || "Adding…", false);

      var sectionIds = [];
      var cic = document.querySelectorAll("cart-items-component[data-section-id]");
      for (var i = 0; i < cic.length; i++) sectionIds.push(cic[i].dataset.sectionId);

      var body = new URLSearchParams();
      body.append("id", variantId);
      body.append("quantity", "1");
      if (sectionIds.length) body.append("sections", sectionIds.join(","));
      body.append("sections_url", window.location.pathname);

      fetch(data.cartAddUrl || "/cart/add", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: body.toString(),
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.status) {
            // server rejected (sold out / limit) - surface it, don't fake success
            busy = false;
            flashLabel(res.description || res.message || (data.soldOut || "Unavailable"), true);
            return;
          }
          return fetch("/cart.js", { headers: { Accept: "application/json" } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              updateBagCount(cart.item_count);
              // Force Horizon's cart-items-component to re-fetch its section fresh
              // from the server (sectionRenderer.renderSection) instead of morphing
              // from /cart/add's `sections` payload. Passing no sections sends the
              // component down its reliable re-fetch path, so the open drawer
              // actually shows the new line item (was opening visually empty).
              dispatchCartUpdate(cart, null);
              flashLabel(data.added || "Added", true);
              busy = false;
              afterAdd();
            });
        })
        .catch(function () {
          // network failure - fall back to a real native submit (adds + navigates)
          busy = false;
          if (form && typeof form.submit === "function") form.submit();
          else window.location.href = data.cartUrl || "/cart";
        });
    }
    function afterAdd() {
      var mode = data.afterAdd || "drawer";
      if (mode === "stay") return;
      if (mode === "cart") { window.location.href = data.cartUrl || "/cart"; return; }
      var drawer = document.querySelector("cart-drawer-component");
      if (drawer && typeof drawer.open === "function") drawer.open();
      else if (!drawer) window.location.href = data.cartUrl || "/cart";
    }

    // Add-to-cart is owned by Horizon's native <product-form-component> (the theme's
    // own tested engine: real POST + CartAddEvent + the cart drawer auto-opens and
    // refreshes). We intentionally do NOT intercept the form submit here anymore - the
    // old hand-rolled fetch is retired. The sticky bar's button simply clicks the real
    // add-to-cart button so it runs the exact same native flow.
    var realAddBtn = section.querySelector("[data-ino-add]");
    var stickyAdd = section.querySelector("[data-ino-sticky-add]");
    // Instant feedback: the add can take a few seconds before the cart drawer
    // opens, and with no visible reaction shoppers tap again and add a second mat.
    // So the bar button locks and reads "Adding…" the moment it is tapped, and
    // unlocks when the cart reports the add (or after a safety timeout).
    var stickyBusy = false;
    var stickyHtml = stickyAdd ? stickyAdd.innerHTML : "";
    var stickyTimer = null;
    function stickyDone() {
      if (!stickyBusy) return;
      stickyBusy = false;
      clearTimeout(stickyTimer);
      stickyAdd.innerHTML = stickyHtml;
      stickyAdd.disabled = false;
      stickyAdd.removeAttribute("aria-busy");
      stickyAdd.classList.remove("is-adding");
    }
    if (stickyAdd) stickyAdd.addEventListener("click", function (e) {
      e.preventDefault();
      // A disabled real button (sold out) ignores .click(), so don't fake "Adding…".
      if (stickyBusy || !realAddBtn || realAddBtn.disabled) return;
      stickyBusy = true;
      stickyAdd.disabled = true;
      stickyAdd.setAttribute("aria-busy", "true");
      stickyAdd.classList.add("is-adding");
      var adding = stickyAdd.getAttribute("data-adding-label") || "Adding…";
      stickyAdd.innerHTML = "<span></span>";
      stickyAdd.firstChild.textContent = adding;
      stickyTimer = setTimeout(stickyDone, 10000);
      realAddBtn.click();
    });
    if (stickyAdd) document.addEventListener("cart:update", function (ev) {
      var d = ev && ev.detail && ev.detail.data;
      if (d && d.source === "product-form-component") stickyDone();
    });

    /* ---- sticky cart show / hide (IntersectionObserver - flicker-free) ----
       The old version recomputed on every scroll pixel via getBoundingClientRect,
       so tiny scroll jitter at the threshold toggled the bar on/off repeatedly
       (the flicker). IntersectionObserver fires only on a real crossing. */
    var sticky = section.querySelector("[data-ino-pdp-sticky]");
    if (sticky) {
      // The quick-buy bar shows only once the real Add-to-Bag button has scrolled
      // ABOVE the viewport (you've passed the buy zone) and STAYS up the whole way
      // down, hiding again at the buy zone (scroll back up) or over the footer.
      // KEY: we test boundingClientRect.bottom <= 0 (button is above the top), NOT
      // mere !isIntersecting - otherwise on MOBILE, where the buy box is below the
      // gallery and starts off-screen on load, the bar would pop up immediately.
      // On desktop the buy box is position:sticky so the button stays in view until
      // the section ends (≈ Technology), exactly where it should appear.
      // "Always visible" forces it on the entire time (still hidden over the footer).
      var always = data.stickyAlways === true;
      var passedBuy = always; // when always-on, treat as already past the buy box
      var footerNear = false;
      function syncSticky() {
        sticky.classList.toggle("show", passedBuy && !footerNear);
      }
      if ("IntersectionObserver" in window) {
        if (!always && realAddBtn) {
          new IntersectionObserver(
            function (entries) {
              var en = entries[entries.length - 1];
              passedBuy = !en.isIntersecting && en.boundingClientRect.bottom <= 0;
              syncSticky();
            },
            { threshold: 0 }
          ).observe(realAddBtn);
        }
        var footerEl = document.querySelector(".ino-footer");
        if (footerEl) {
          new IntersectionObserver(
            function (entries) {
              footerNear = entries[entries.length - 1].isIntersecting;
              syncSticky();
            },
            { threshold: 0 }
          ).observe(footerEl);
        }
      }
      syncSticky(); // initial paint
    }
    window.addEventListener("resize", function () { scrollToIdx(false); });

    /* ---- buy-box highlight expandables (e.g. Shipping & Returns) ----
       Same accordion mechanism as the FAQ: a highlight row that has detail text
       gets a +/- toggle that reveals it. Compact rows (value only) are untouched. */
    var hlExpand = section.querySelectorAll(".buy2 .hl-row.hl-expand");
    for (var hi = 0; hi < hlExpand.length; hi++) {
      (function (row) {
        if (row.dataset.inoBound) return;
        row.dataset.inoBound = "1";
        var btn = row.querySelector(".hl-toggle");
        var panel = row.querySelector(".hl-detail");
        if (!btn || !panel) return;
        btn.addEventListener("click", function () {
          var open = row.classList.toggle("open");
          btn.setAttribute("aria-expanded", open ? "true" : "false");
          var inner = panel.querySelector(".inner");
          panel.style.maxHeight = open && inner ? inner.scrollHeight + "px" : "0px";
        });
      })(hlExpand[hi]);
    }

    // init
    renderSlides();
    applyColorway();
  }

  /* ---- FAQ accordion (scoped to .ino-faq) ---- */
  function initFaq(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var items = root.querySelectorAll(".ino-faq .faq-item");
    if (root.matches && root.matches(".ino-faq .faq-item")) {
      // (scope itself is an item - rare, ignore)
    }
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        if (item.dataset.inoBound) return;
        item.dataset.inoBound = "1";
        var btn = item.querySelector(".faq-q");
        var panel = item.querySelector(".faq-a");
        if (!btn || !panel) return;
        btn.addEventListener("click", function () {
          var open = item.classList.toggle("open");
          btn.setAttribute("aria-expanded", open ? "true" : "false");
          var inner = panel.querySelector(".inner");
          panel.style.maxHeight = open && inner ? inner.scrollHeight + "px" : "0px";
        });
      })(items[i]);
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    try {
      var sections = scope.querySelectorAll(".ino-pdp");
      if (scope.matches && scope.matches(".ino-pdp")) initPdp(scope);
      for (var i = 0; i < sections.length; i++) initPdp(sections[i]);
    } catch (e) {}
    try { initFaq(scope); } catch (e) {}
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); });
  } else {
    init(document);
  }

  // Theme-editor lifecycle: re-wire a reloaded section subtree.
  document.addEventListener("shopify:section:load", function (e) {
    init((e && e.target) || document);
  });

  // The PDP buy box uses Horizon's native <product-form-component>. Guarantee the cart
  // drawer opens on a successful add even if the theme's auto-open-drawer setting is off,
  // so adding always shows the bag (matches the INO flow). Idempotent if auto-open is on.
  document.addEventListener("cart:update", function (e) {
    var d = e && e.detail && e.detail.data;
    if (!d || d.source !== "product-form-component" || d.didError) return;
    var drawer = document.querySelector("cart-drawer-component");
    if (drawer && typeof drawer.open === "function") drawer.open();
  });
})();
